import { ChakraProvider } from '@chakra-ui/react';
import type { Editor, JSONContent } from '@tiptap/core';
import { callV2Theme } from '@web-app/theming/theme';
import dynamic from 'next/dynamic';
import React, { useCallback, useRef } from 'react';

import { EditorName } from '../TipTap';

/**
 * TipTap editor wrapper. Temporary shim until Quill editor is fully deprecated.
 */
const TipTapEditor = dynamic(
  () =>
    import('@web-app/components/TipTap/TipTapEditor').then(
      (mod) => mod.TipTapEditor,
    ),
  { ssr: false },
);

interface EditorV2Props {
  value?: string | object; // Can be HTML string or TipTap JSON object
  onChange: (html: string, json?: JSONContent) => void;
  onEditorReady?: (editor: Editor) => void;
  placeholder?: string;
  height?: string | number;
  readOnly?: boolean;
  showToolbar?: boolean;
  showBorder?: boolean;
  'data-testid'?: string;
  name: EditorName;
  /** See {@link UseTipTapEditorOptions.onImageFiles}. */
  onImageFiles?: (files: File[]) => void;
}

export const EditorV2: React.FC<EditorV2Props> = ({
  value = '',
  onChange,
  onEditorReady,
  placeholder,
  readOnly = false,
  showToolbar = true,
  showBorder = true,
  name,
  onImageFiles,
}) => {
  // Tracks last HTML value sent to parent to prevent infinite loops
  // (onUpdate fires on every editor transaction, even non-content changes like selection)
  const lastHtmlRef = useRef<string>('');
  const handleChange = useCallback(
    (html: string, json: JSONContent) => {
      // Only notify parent if content actually changed
      if (html !== lastHtmlRef.current) {
        lastHtmlRef.current = html;
        onChange(html, json);
      }
    },
    [onChange],
  );

  return (
    <ChakraProvider theme={callV2Theme}>
      <TipTapEditor
        name={name}
        content={value}
        onImageFiles={onImageFiles}
        onChange={handleChange}
        onEditorReady={onEditorReady}
        placeholder={placeholder}
        editable={!readOnly}
        showToolbar={showToolbar}
        showBorder={showBorder}
      />
    </ChakraProvider>
  );
};
